import React, { useCallback, useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  Button,
  Link,
  IconButton,
} from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { TRequestTableDataByTableType } from '../../../types/mvpCharts';
import {
  useAddOrUpdateDataByTableTypeMutation,
  useLazyGetTableCSVPresignedURLByTableTypeQuery,
} from '../../../services/mVPGraphQlService';
import { showAlert } from '../../../services/alert';
import { useAppDispatch } from '../../../storeHook';
import arrow from '../../../assets/icons/line_arrow-close-left.svg';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';

type ImportUsersDrawerProps = {
  open: boolean;
  onClose: () => void;
  type: string;
  refetch: any;
};

export default function ImportUsersDrawer({
  open,
  onClose,
  type,
  refetch,
}: ImportUsersDrawerProps) {
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string>('');
  const [reasons, setReasons] = useState<string>('');
  const [uploadFailed, setUploadFailed] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  // const [errorDownloadUrl, setErrorDownloadUrl] = useState<
  //   string | null
  // >(null);

  const dispatch = useAppDispatch();
  const wsId = useSelector(
    (state: RootState) => state.country.wsId
  );
  const queryParams: TRequestTableDataByTableType = {
    tableType: type == 'user' ? 'TT_USER' : type == 'agriInput' ? 'TT_AGRI_INPUT_PRODUCTS' : type == 'agriInputSchedule' ? 'TT_AGRI_INPUT_SCHEDULE_ITEMS': 'TT_GEOGRAPHY',
    count: 0,
    csvFileType: 'TEMPLATE_FILE',
    wsId: wsId,
  };

  const [getCSVUrl, { isFetching }] =
    useLazyGetTableCSVPresignedURLByTableTypeQuery();

  const handleFileSelect = useCallback(
    (selectedFile: File) => {
      setUploadSuccess(false); // reset success banner
      setUploadFailed(false);

      if (selectedFile.type !== 'text/csv') {
        setError('Only CSV files are allowed.');
        return;
      }
      if (selectedFile.size > 500 * 1024) {
        setError('File size must be less than 500KB.');
        return;
      }
      setError('');
      setFile(selectedFile);
    },
    []
  );

  const handleDrop = (
    event: React.DragEvent<HTMLDivElement>
  ) => {
    event.preventDefault();
    if (event.dataTransfer.files.length > 0) {
      handleFileSelect(event.dataTransfer.files[0]);
    }
  };

  const handleBrowse = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.files && event.target.files[0]) {
      handleFileSelect(event.target.files[0]);
    }
  };

  const [addorUpdateRequest] =
    useAddOrUpdateDataByTableTypeMutation();
  const handleUploadCSV = async () => {
    if (!file) return;

    try {
      const response = await getCSVUrl({
        ...queryParams,
        count: 1,
        csvFileType: 'UPLOADED_FILE',
        wsId: wsId,
      }).unwrap();

      const { fileUrl, fileKey } =
        response?.data?.GetTableCSVPresignedURLByTableType
          ?.fileUploads?.[0] || {};

      if (!fileUrl || !fileKey) {
        throw new Error('Invalid upload response');
      }

      // 2️⃣ Upload file using PUT
      const uploadResponse = await fetch(fileUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': 'text/csv',
        },
        body: file,
      });

      if (!uploadResponse.ok) {
        throw new Error('File upload failed');
      }

      const obj = {
        dataType: 'DT_CSV',
        tableType: type == 'user' ? 'TT_USER' : type == 'agriInput' ? 'TT_AGRI_INPUT_PRODUCTS' : type == 'agriInputSchedule' ? 'TT_AGRI_INPUT_SCHEDULE_ITEMS': 'TT_GEOGRAPHY',
        fileKey: fileKey,
        wsId: wsId,
      };

      addorUpdateRequest(obj)
        .unwrap()
        .then((response) => {
          const inspectionData =
            response?.data?.AddOrUpdateDataByTableType;

          if (
            inspectionData?.responseStatus?.status ===
            'SUCCESS'
          ) {
            setUploadSuccess(true);
            setUploadFailed(false);

            setTimeout(() => {
              onClose();
              resetFile();
            }, 1500);
            setError('');
            setReasons('');
            dispatch(
              showAlert({
                content: `${
                  inspectionData?.insertedCount
                } ${
                  type == 'user'
                    ? 'users'
                    : type == 'agriInput'
                    ? 'products'
                    : 'geographies'
                } imported Successfully`,
              })
            );
            refetch();
          } else {
            setError(
              inspectionData?.responseStatus
                ?.errorMessages?.[0]
            );
            setReasons(
              inspectionData?.responseStatus?.reasons?.[0]
            );
            setUploadFailed(true);
          }
        })

        .catch(() => {
          setUploadFailed(true);
        });
    } catch (err) {
      console.error('CSV upload failed', err);
    }
  };

  const resetFile = () => {
    setFile(null);
    setError('');
  };
  // const handleErrorFileDownload = () => {
  //   if (!errorDownloadUrl) return;

  //   const baseUrl = process.env.REACT_APP_API_BASE_URL;
  //   const fullUrl = errorDownloadUrl.startsWith('http')
  //     ? errorDownloadUrl
  //     : `${baseUrl}/${errorDownloadUrl}`;

  //   window.open(fullUrl, '_blank');
  // };

  const handleDownloadTemplate = async () => {
    try {
      const response = await getCSVUrl(
        queryParams
      ).unwrap();

      const downloadUrl =
        response?.data?.GetTableCSVPresignedURLByTableType
          ?.templateUrl;

      if (!downloadUrl) {
        throw new Error('Download URL not found');
      }

      window.open(downloadUrl, '_blank');
    } catch (err) {
      console.error('Download template failed', err);
    }
  };
  const fileInputRef =
    React.useRef<HTMLInputElement | null>(null);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: '50vw',
          backgroundColor: '#fff',
        },
      }}>
      {/* Header */}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          borderBottom: '1px solid #E0E0E0',
          boxShadow: '0px 2px 6px rgba(0,0,0,0.04)',
        }}>
        <Typography
          // variant="h6"
          fontSize={17}
          fontWeight={600}
          sx={{ lineHeight: 1 }}>
          {type == 'user'
            ? 'Import Users'
            : type == 'agriInput' ? 'Import Products'
            : type == 'agriInputSchedule' ? 'Import Schedule'
            : 'Import Geography'}
        </Typography>

        <Box
          onClick={onClose}
          sx={{
            width: 24,
            height: 24,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}>
          <img
            src={arrow}
            alt="close"
            style={{ width: 20, height: 20 }}
          />
        </Box>
      </Box>
      <Box sx={{ p: 3 }}>
        {/* Upload Section */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1,
          }}>
          <Typography
            variant="body2"
            fontWeight={500}
            color="#444">
            Upload File
          </Typography>
          <Link
            component="button"
            underline="none"
            onClick={handleDownloadTemplate}
            sx={{
              fontSize: 14,
              color: 'rgba(0,105,112,1)',
              fontWeight: 500,
            }}>
            {isFetching
              ? 'Downloading...'
              : 'Download Template'}
          </Link>
        </Box>

        {/* Upload Box */}
        <Box
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          sx={{
            border: '1px dashed #ccc',
            borderRadius: 2,
            p: 4,
            textAlign: 'center',
            backgroundColor: '#fafafa',
            cursor: 'pointer',
            position: 'relative',
          }}>
          {!file ? (
            <div onClick={() => handleBrowse}>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleBrowse}
                style={{
                  opacity: 0,
                  position: 'absolute',
                  inset: 0,
                  cursor: 'pointer',
                }}
              />
              <CloudUploadOutlinedIcon
                sx={{ fontSize: 36, color: '#777', mb: 1 }}
              />
              <Typography
                variant="body2"
                sx={{ color: '#666', mb: 0.5 }}>
                Drag & drop files or{' '}
                <Link
                  component="button"
                  underline="none"
                  sx={{
                    color: 'rgba(0,105,112,1)',
                    fontWeight: 500,
                    cursor: 'pointer',
                  }}
                  onClick={() =>
                    fileInputRef.current?.click()
                  }>
                  browse
                </Link>
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: '#999' }}>
                Supported formats: CSV (max. size 500KB)
              </Typography>
            </div>
          ) : (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: '#fefefe',
                border: '1px solid #ddd',
                borderRadius: 1.5,
                px: 2,
                py: 1.5,
              }}>
              <Box>
                <Typography
                  fontSize={14}
                  fontWeight={500}
                  color="#333">
                  {file.name}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: '#999' }}>
                  {(file.size / 1024).toFixed(1)} KB
                </Typography>
              </Box>
              <IconButton size="small" onClick={resetFile}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
          )}

          {error && (
            <Typography
              variant="caption"
              color="error"
              sx={{ mt: 1, display: 'block' }}>
              {error}
            </Typography>
          )}
        </Box>

        {/* Footer */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1,
            mt: 4,
          }}>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!file}
            sx={{
              backgroundColor: 'rgba(0,105,112,1)',
              '&:hover': {
                backgroundColor: 'rgba(0,105,112,0.9)',
              },
            }}
            onClick={handleUploadCSV}>
            Upload
          </Button>
        </Box>
        {uploadFailed && file && (
          <Box
            sx={{
              mt: 2,
              border: '1px solid #f04438',
              borderRadius: 1,
              px: 2,
              py: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: '#fff5f5',
            }}>
            <Box display="flex" alignItems="center" gap={1}>
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  backgroundColor: '#f04438',
                  color: '#fff',
                  fontSize: 12,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 600,
                }}>
                !
              </Box>

              <Typography fontSize={14} color="#d92d20">
                {reasons}
                <br />
                File upload failed.{' '}
                {error ? `: ${error}` : ''}
              </Typography>
            </Box>

            {/* {errorDownloadUrl && (
            <Button
              variant="text"
              onClick={handleErrorFileDownload}
              sx={{
                color: '#d92d20',
                fontWeight: 500,
                textTransform: 'none',
              }}>
              ⬇ Download File
            </Button>
          )} */}
          </Box>
        )}
        {uploadSuccess && file && (
          <Box
            sx={{
              mt: 2,
              border: '1px solid #12B76A',
              borderRadius: 1,
              px: 2,
              py: 1.5,
              display: 'flex',
              alignItems: 'center',
              backgroundColor: '#ECFDF3',
              gap: 1,
            }}>
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                backgroundColor: '#12B76A',
                color: '#fff',
                fontSize: 12,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 700,
              }}>
              ✓
            </Box>

            <Typography fontSize={14} color="#027A48">
              File uploaded successfully
            </Typography>
          </Box>
        )}
      </Box>
    </Drawer>
  );
}
